// §27 — Reminders: shared task lists, and the groups you share them with.
//
// This is the first thing in Hangar that **two people can both write to**. §26 built a social graph
// where the only shared object was a message — append-only, owned by nobody, deleted by either.
// A reminder list is different: it has an owner, a membership, and rows that either party may edit,
// so "who may do what" stops being a yes/no and becomes a role.
//
// Three rules carry the whole design, and the rest is arithmetic on them:
//
//   1. **Access is resolved from the database, never from a parameter** (§26.9, D-046). A list is
//      visible if you own it, if it is shared with you by ref, or if it is shared with a group you
//      are in — and the last of those is why membership is a table rather than a column.
//   2. **You may only share with an accepted connection.** There is no directory (§26.3), so a ref
//      you could share with is a ref you already know; anything else would be a way to discover
//      whether an account exists by watching which shares are accepted.
//   3. **The owner owns the sharing.** An editor may change every item and no share. Otherwise the
//      person you shared a shopping list with can hand it to somebody you have never met.

/** What a caller may do with a list, resolved per request and never stored on the client. */
export const REMINDER_ROLES = ['owner', 'editor', 'viewer'] as const;
export type ReminderRole = (typeof REMINDER_ROLES)[number];

export const canEditItems = (role: ReminderRole): boolean => role !== 'viewer';
export const canManageShares = (role: ReminderRole): boolean => role === 'owner';

/** §27 — who a list is shared with: a person, or one of the owner's groups. */
export const SHARE_SUBJECTS = ['user', 'group'] as const;
export type ShareSubject = (typeof SHARE_SUBJECTS)[number];

export interface ReminderList {
  readonly id: string;
  readonly name: string;
  /** 0–359. A list is found by colour before it is found by name, so it is not decoration. */
  readonly hue: number;
  readonly owner: string;
  readonly ownerName: string;
  /** The **caller's** role. The same row is `owner` to one person and `viewer` to another. */
  readonly role: ReminderRole;
  readonly createdAt: number;
  readonly updatedAt: number;
  /** Items not yet done — what the list badge shows. */
  readonly openCount: number;
  /** True when anybody but the owner can see it. Drives the "shared" mark in the sidebar. */
  readonly shared: boolean;
}

export interface Reminder {
  readonly id: string;
  readonly listId: string;
  readonly title: string;
  readonly notes: string;
  /** Local instant, or `null`. A reminder without one is a task; with one it is an appointment. */
  readonly dueAt: number | null;
  readonly completedAt: number | null;
  /**
   * Who ticked it, as a ref, or `null`.
   *
   * The single most useful thing a *shared* list adds over a private one: "did somebody already
   * buy the milk" is the question, and the tick alone does not answer it.
   */
  readonly completedBy: string | null;
  readonly completedByName: string | null;
  readonly createdBy: string;
  readonly createdByName: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  /** Manual order within the list. Sparse, so a move between two rows needs no renumbering. */
  readonly position: number;
}

export interface UserGroup {
  readonly id: string;
  readonly name: string;
  readonly owner: string;
  readonly createdAt: number;
  readonly members: readonly { readonly ref: string; readonly displayName: string }[];
}

export interface ReminderShare {
  readonly kind: ShareSubject;
  /** A user ref, or a group id. */
  readonly id: string;
  /** The person's or the group's name, resolved for display. */
  readonly name: string;
  readonly canEdit: boolean;
  readonly createdAt: number;
}

// ── Limits ───────────────────────────────────────────────────────────────────────────────────

/** Long enough for a sentence, short enough that a title is still a title. */
export const MAX_REMINDER_TITLE = 512;
/** §26.5's message limit, for the same reason: notes are prose, and prose has to stop somewhere. */
export const MAX_REMINDER_NOTES = 4096;
export const MAX_LIST_NAME = 120;
export const MAX_GROUP_NAME = 120;

/**
 * The gap between two reminders' positions.
 *
 * Sparse on purpose: dropping a row between two others takes the midpoint, so an ordinary reorder
 * writes **one** row instead of renumbering the list — which matters most on the list two people
 * are both dragging things around in.
 */
export const POSITION_STEP = 1024;

/** The position for a row inserted between `before` and `after`, either of which may be absent. */
export function positionBetween(before: number | null, after: number | null): number {
  if (before === null && after === null) return POSITION_STEP;
  if (before === null) return (after as number) - POSITION_STEP;
  if (after === null) return before + POSITION_STEP;
  return Math.round((before + after) / 2);
}

/**
 * `true` when two adjacent positions have run out of room between them.
 *
 * Midpoints halve the gap each time, so about ten insertions in the same place exhaust it. The
 * answer is to renumber the list, and the caller has to be told rather than left writing rows that
 * silently land in the wrong order.
 */
export const needsRenumber = (before: number, after: number): boolean =>
  Math.abs(after - before) <= 1;

// ── Sorting and grouping, shared by every view ───────────────────────────────────────────────

/**
 * The order a list is read in: unfinished first by manual position, then finished by when.
 *
 * Completed items sink rather than disappear, because the useful thing about a shared list is
 * seeing that somebody already did it. They are *not* re-sorted into the manual order — a tick
 * should move a row once, not shuffle the rest around it.
 */
export function sortReminders(items: readonly Reminder[]): Reminder[] {
  return [...items].sort((a, b) => {
    if ((a.completedAt === null) !== (b.completedAt === null)) return a.completedAt === null ? -1 : 1;
    if (a.completedAt !== null && b.completedAt !== null) return b.completedAt - a.completedAt;
    if (a.position !== b.position) return a.position - b.position;
    return a.createdAt - b.createdAt;
  });
}

export type DueBucket = 'overdue' | 'today' | 'soon' | 'later' | 'none';

/**
 * Which bucket a due date falls in, from `now`.
 *
 * Buckets rather than a countdown because that is what a person does with a due date: it is late,
 * it is today, it is coming, or it is a someday. `soon` is the next seven days.
 */
export function dueBucket(dueAt: number | null, now: number): DueBucket {
  if (dueAt === null) return 'none';
  if (dueAt < now) return 'overdue';
  const start = new Date(now);
  const endOfToday = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate() + 1,
    0,
    0,
    0,
    0,
  ).getTime();
  if (dueAt < endOfToday) return 'today';
  return dueAt < endOfToday + 7 * 86_400_000 ? 'soon' : 'later';
}

/** `Today 17:00` · `Tue 09:30` · `12 Mar` — a due date at the length a row can hold. */
export function formatDue(dueAt: number, now: number): string {
  const date = new Date(dueAt);
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const bucket = dueBucket(dueAt, now);
  if (bucket === 'today') return `Today ${time}`;
  if (bucket === 'overdue' || bucket === 'soon') {
    return `${date.toLocaleDateString([], { weekday: 'short' })} ${time}`;
  }
  return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

/**
 * What somebody typed into a due-date box: a date, a date and a time, or nothing.
 *
 * `<input type="datetime-local">` gives `2026-03-12T17:00`, which `new Date` reads as **local**
 * time — which is what a due date means. Anything else answers `null`, so the form can refuse
 * rather than store a date nobody chose.
 */
export function parseDue(input: string): number | null {
  const text = input.trim();
  if (text.length === 0) return null;
  const at = new Date(text).getTime();
  return Number.isFinite(at) ? at : null;
}

/** The value a `datetime-local` input wants, from an instant. Local, and to the minute. */
export function toDueInput(dueAt: number | null): string {
  if (dueAt === null) return '';
  const date = new Date(dueAt);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}
